import { CheckCircle2, Lock, XCircle } from "lucide-react";

const tone = (s) => (s >= 80 ? "text-emerald-400" : s >= 60 ? "text-[#F3C649]" : "text-red-400");

const Ring = ({ v, label, testid }) => {
  const r = 30, c = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative h-24 w-24">
        <svg viewBox="0 0 72 72" className="h-24 w-24 -rotate-90">
          <circle cx="36" cy="36" r={r} stroke="#27272A" strokeWidth="6" fill="none" />
          {v != null && <circle cx="36" cy="36" r={r} stroke="#D4AF37" strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - v / 100)} className="lp-ring" style={{ "--c": c }} />}
        </svg>
        <span className={`absolute inset-0 flex items-center justify-center font-mono text-2xl font-bold ${v == null ? "text-zinc-600" : tone(v)}`} data-testid={testid}>{v ?? <Lock className="h-5 w-5" />}</span>
      </div>
      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">{label}</span>
    </div>
  );
};

const Chips = ({ title, items, ok, testid }) => items?.length > 0 && (
  <div data-testid={testid}>
    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">{title}</div>
    <div className="flex flex-wrap gap-1.5">{items.map((s, i) => <span key={`${s}-${i}`} className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-red-500/30 bg-red-500/10 text-red-300"}`}>{ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}{s}</span>)}</div>
  </div>
);

export const FitResult = ({ r, onCta }) => {
  const fit = r.fit;
  return (
    <div className="fade-up space-y-6" data-testid="fit-result">
      <div className="flex flex-wrap items-center justify-around gap-6 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#33290D] to-[#141108] p-6">
        <Ring v={r.score} label="Resume score" testid="fit-resume-score" />
        <Ring v={fit?.score} label={`Fit: ${r.target_role}`} testid="fit-job-score" />
      </div>
      {(fit?.summary || r.summary) && <p className="text-sm text-zinc-300" data-testid="fit-summary">{fit?.summary || r.summary}</p>}
      {fit ? (
        <div className="grid gap-5 sm:grid-cols-2">
          <Chips title="Strengths for this role" items={fit.matched_skills} ok testid="fit-matched" />
          <Chips title="Gaps to close" items={fit.missing_skills} testid="fit-missing" />
          {fit.suggested_roles?.length > 0 && <div className="sm:col-span-2" data-testid="fit-roles"><div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">Roles you're a strong fit for</div><p className="text-sm text-zinc-200">{fit.suggested_roles.join(" · ")}</p></div>}
        </div>
      ) : <p className="rounded-xl border border-dashed border-[#D4AF37]/30 p-4 text-sm text-zinc-400" data-testid="fit-locked">Your personalised job-fit analysis for <span className="text-[#F3C649]">{r.target_role}</span> unlocks in your free account.</p>}
      {(fit?.next_steps || r.fixes)?.length > 0 && (
        <div data-testid="fit-fixes"><div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">Your top next steps</div>
          <ul className="space-y-1.5 text-sm text-zinc-300">{(fit?.next_steps || r.fixes).map((f) => <li key={f} className="flex gap-2"><span className="text-[#D4AF37]">→</span>{f}</li>)}</ul></div>
      )}
      <button onClick={onCta} data-testid="fit-signup-button"
        className="lp-shine relative w-full overflow-hidden rounded-full bg-gradient-to-r from-[#D4AF37] via-[#F3C649] to-[#D4AF37] px-6 py-3.5 text-sm font-semibold text-black shadow-[0_0_30px_rgba(212,175,55,0.3)]">
        Create my free account and fix these
      </button>
      <p className="text-center text-[11px] text-zinc-500">Free account. No card needed. Scores reflect RPOPRO's own criteria, not a guarantee of interviews.</p>
    </div>
  );
};
